import React, { useState, useReducer } from 'react';
import './JoinAuth.css'; // shared auth styles (.mfa-*, .auth-error) travel with this component
import { multiFactor } from 'firebase/auth';
import {
  startMfaEnrollment,
  finishMfaEnrollment,
  sendVerificationEmail,
  clearRecaptcha,
  friendlyAuthError,
} from '../firebase/auth';
import { trackEvent } from '../firebase/analytics';

const ENROLL_RECAPTCHA_ID = 'recaptcha-mfa-enroll';

// Whether this account's primary sign-in can carry an SMS second factor.
// Phone-only accounts can't (SMS can't be both first and second factor).
const canUseMfa = (user) =>
  (user.providerData || []).some((p) => p.providerId !== 'phone');

const MfaEnroll = ({ user }) => {
  const [, refreshTick] = useReducer((x) => x + 1, 0);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'phone' | 'code'
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const enrolled = multiFactor(user).enrolledFactors || [];

  const run = async (label, fn) => {
    setError('');
    setInfo('');
    setBusy(label);
    try {
      await fn();
    } catch (err) {
      setError(friendlyAuthError(err));
      trackEvent('mfa_error', { action: label, code: err?.code || 'unknown' });
    } finally {
      setBusy('');
    }
  };

  const refresh = async () => {
    await user.reload();
    refreshTick();
  };

  const handleResendEmail = () =>
    run('verify_email', async () => {
      await sendVerificationEmail(user);
      setInfo('Verification email sent. Open the link, then tap “I’ve verified”.');
    });

  const handleRefreshVerified = () =>
    run('refresh', async () => {
      await refresh();
    });

  const handleSendEnrollCode = (e) => {
    e.preventDefault();
    run('enroll_send', async () => {
      const id = await startMfaEnrollment(user, phone.trim(), ENROLL_RECAPTCHA_ID);
      setVerificationId(id);
      setPhase('code');
    });
  };

  const handleConfirmEnroll = (e) => {
    e.preventDefault();
    run('enroll_confirm', async () => {
      await finishMfaEnrollment(user, verificationId, code.trim(), 'My phone');
      trackEvent('mfa_enrolled', {});
      setPhase('idle');
      setPhone('');
      setCode('');
      setVerificationId(null);
      clearRecaptcha(ENROLL_RECAPTCHA_ID);
      await refresh();
    });
  };

  const handleRemove = (factor) =>
    run('unenroll', async () => {
      await multiFactor(user).unenroll(factor);
      await refresh();
    });

  const cancelEnroll = () => {
    setPhase('idle');
    setCode('');
    setVerificationId(null);
    setError('');
    clearRecaptcha(ENROLL_RECAPTCHA_ID);
  };

  if (!canUseMfa(user)) return null;

  return (
    <div className="mfa-enroll">
      <div className="mfa-enroll-head">
        <span className="mfa-title">Two-step verification</span>
        <span className={`mfa-badge ${enrolled.length ? 'on' : 'off'}`}>
          {enrolled.length ? 'On' : 'Off'}
        </span>
      </div>

      {/* Enrolled factors */}
      {enrolled.map((f) => (
        <div className="mfa-factor" key={f.uid}>
          <span>📱 {f.phoneNumber || f.displayName || 'Phone'}</span>
          <button
            type="button"
            className="mfa-link"
            onClick={() => handleRemove(f)}
            disabled={!!busy}
          >
            Remove
          </button>
        </div>
      ))}

      {/* Email must be verified before enrolling */}
      {!user.emailVerified ? (
        <div className="mfa-note">
          <p>Verify your email first to turn on two-step verification.</p>
          <div className="mfa-actions">
            <button
              type="button"
              className="mfa-link"
              onClick={handleResendEmail}
              disabled={!!busy}
            >
              {busy === 'verify_email' ? 'Sending…' : 'Send verification email'}
            </button>
            <button
              type="button"
              className="mfa-link"
              onClick={handleRefreshVerified}
              disabled={!!busy}
            >
              {busy === 'refresh' ? 'Checking…' : 'I’ve verified'}
            </button>
          </div>
        </div>
      ) : (
        <>
          {phase === 'idle' && (
            <button
              type="button"
              className="mfa-add-btn"
              onClick={() => setPhase('phone')}
              disabled={!!busy}
            >
              {enrolled.length ? 'Add another phone' : 'Add a phone number'}
            </button>
          )}

          {phase === 'phone' && (
            <form className="mfa-form" onSubmit={handleSendEnrollCode}>
              <input
                type="tel"
                placeholder="+1 555 123 4567"
                aria-label="Phone number for two-step verification"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
              <div className="mfa-actions">
                <button className="btn-primary" type="submit" disabled={!!busy}>
                  {busy === 'enroll_send' ? 'Sending…' : 'Send code'}
                </button>
                <button type="button" className="mfa-link" onClick={cancelEnroll}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {phase === 'code' && (
            <form className="mfa-form" onSubmit={handleConfirmEnroll}>
              <input
                type="text"
                inputMode="numeric"
                placeholder="6-digit code"
                aria-label="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
              <div className="mfa-actions">
                <button className="btn-primary" type="submit" disabled={!!busy}>
                  {busy === 'enroll_confirm' ? 'Verifying…' : 'Verify & enable'}
                </button>
                <button type="button" className="mfa-link" onClick={cancelEnroll}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {info && <p className="mfa-info">{info}</p>}
      {error && <p className="auth-error">{error}</p>}
      <div id={ENROLL_RECAPTCHA_ID} />
    </div>
  );
};

export default MfaEnroll;
